import { useEffect, useRef } from 'react';
import { presenceService } from '../realtime';
import { useAppDispatch, useAppSelector } from '../store';
import { clearActiveTask, getActiveTask, setActiveTask } from '../store/presenceSlice';
import { getPageId } from '../store/tasksSlice';
import { getUser } from '../store/userSlice';

const TASK_ACTIVITY_TIMEOUT = 3000; // 3 seconds
const CLEANUP_INTERVAL = 1000; // 1 second

// Whisper data type
interface TaskActivityWhisperData {
  userId: string;
  taskId: string;
  timestamp: number;
}

export const useCollaborativeActiveTask = () => {
  const currentUser = useAppSelector(getUser);
  const activeTask = useAppSelector(getActiveTask);
  const dispatch = useAppDispatch();
  const cleanupIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pageId = useAppSelector(getPageId);

  // Generate a unique subscriber ID for this hook instance
  const subscriberIdRef = useRef(`active-task-${Math.random().toString(36).substr(2, 9)}`);
  const subscriberId = subscriberIdRef.current;

  const channelName = pageId ? `pages.${pageId}` : null;

  useEffect(() => {
    if (!pageId || !currentUser || !window.Echo || !channelName) {
      return;
    }

    const presenceChannel = presenceService.register(channelName, subscriberId);
    if (!presenceChannel) return;

    const handleTaskActivity = (payload: TaskActivityWhisperData) => {
      if (payload.userId === currentUser.id) {
        return;
      }

      dispatch(setActiveTask({
        userId: payload.userId,
        taskId: payload.taskId || '',
        timestamp: Date.now(),
      }));
    };

    const cleanupStaleActivity = () => {
      const now = Date.now();

      if (activeTask && activeTask.userId === currentUser.id) {
        return;
      }

      if (activeTask && now - activeTask.timestamp > TASK_ACTIVITY_TIMEOUT) {
        dispatch(clearActiveTask());
      }
    };

    presenceChannel.listenForWhisper('active-task', handleTaskActivity);

    // Note: Presence callbacks (here/joining/leaving) are now handled centrally 
    // in the presence channel manager and update Redux automatically

    cleanupIntervalRef.current = setInterval(cleanupStaleActivity, CLEANUP_INTERVAL);

    return () => {
      if (presenceChannel) {
        try { presenceChannel.stopListeningForWhisper('active-task'); } catch (_e) { /* noop */ }
      }

      if (cleanupIntervalRef.current) {
        clearInterval(cleanupIntervalRef.current);
        cleanupIntervalRef.current = null;
      }
    };
  }, [pageId, currentUser, dispatch, activeTask, channelName, subscriberId]);

  // Cleanup subscriber on unmount
  useEffect(() => {
    return () => {
      if (channelName) presenceService.unregister(channelName, subscriberId);
    };
  }, [channelName, subscriberId]);
};